import { ButtonV2, DataGridColumn, DataGridRef, TooltipV2 } from '@cimplico/components';
import { AnimatePresence, motion } from 'framer-motion';
import { FieldType, WorksheetSchemaTable } from '../../../../../types/custom-worksheet-v2';
import { sortByOrder } from '../../store/utils';

import { useIsCollapsed } from '../../hooks/use-is-collapsed';
import { useDispatch, useSelector } from 'react-redux';
import { selectCollapseAll, selectShowHidden } from '../../store/selectors';
import { addRow, removeRows } from '../../store/slice';
import { useRef, useState } from 'react';
import FlashBackground from '../FlashBackground';
import FieldIcon from '../../../../../components/custom-worksheet/FieldIcon';
import PropertyHeader from '../PropertyHeader';
import { TableBody, TableHead, TableHeader, Table as DTable, TableRow, TableCell } from '../../../../../components/data-table/DataTable';
import TableField from './TableField';
import { PlusIcon, QuestionMarkCircleIcon } from '@heroicons/react/20/solid';
import TableSummary from './TableSummary';
import { FieldValue } from '../field/CWReadField';
import { isNullOrUndefined } from '../../../../new-workpaper/utils';

type TableProps = {
    table: WorksheetSchemaTable;
};

function Table({ table }: TableProps): JSX.Element {
    const defaultCollapsed = useSelector(selectCollapseAll);
    const showHidden = useSelector(selectShowHidden);
    const [isCollapsed, setIsCollapsed] = useIsCollapsed(defaultCollapsed === undefined ? false : defaultCollapsed);
    const [selectedRows, setSelectedRows] = useState<string[]>([]);
    const tableRef = useRef<DataGridRef>(null);

    const dispatch = useDispatch();

    function handleNew(): void {
        dispatch(addRow(table.id));
    }

    function handleRemove(): void {
        dispatch(removeRows(selectedRows));
        tableRef.current?.clear();
    }

    if (!('properties' in table.row) || Object.keys(table.row.properties).length === 0) {
        return <></>;
    }

    const columns = sortByOrder(table.row.properties)
        .filter((header) => (header.hidden ? showHidden : true))
        .map((header, idx) => {
            return {
                alignment: header.styles.alignment,
                header: (
                    <div className="w-fit">
                        <TooltipV2 disabled={!header.helperText} message={header.helperText ?? ''} position="top">
                            <div className="flex items-center w-fit gap-1" style={{ justifyContent: header.styles.alignment }}>
                                <div className="shrink-0 w-4 h-4 text-gray-500">
                                    <FieldIcon type={header.type} />
                                </div>
                                <label className="text-xs text-gray-800 font-medium uppercase">{header.name}</label>
                            </div>
                        </TooltipV2>
                    </div>
                ),
                width: header.styles.width,
                pinned: idx === 0,
            };
        });

    return (
        <div id={table.id} className="scroll-mt-8 bg-white rounded-[5px] flex flex-col relative w-full">
            <FlashBackground id={table.id} />
            <PropertyHeader
                headingSize="base"
                property={table}
                isCollapsed={isCollapsed}
                onCollapse={setIsCollapsed}
                onAdd={table.generatedFromFunction ? undefined : handleNew}
                onRemove={table.generatedFromFunction ? undefined : handleRemove}
                selectedRows={selectedRows}
            />
            <AnimatePresence initial={false}>
                {!isCollapsed && (
                    <motion.div
                        className="w-full pl-6"
                        exit="closed"
                        initial="closed"
                        animate="open"
                        transition={{ duration: 0.2 }}
                        variants={{
                            open: {
                                height: 'auto',
                                opacity: 1,
                            },
                            closed: {
                                height: 0,
                                opacity: 0,
                                className: 'overflow-hidden',
                            },
                        }}
                    >
                        <DTable onCheck={table.generatedFromFunction ? undefined : setSelectedRows}>
                            <TableHead>
                                {columns.map((col, idx) => {
                                    return (
                                        <TableHeader key={idx} cellIndex={idx + 1} align={col.alignment} pinned={col.pinned}>
                                            {col.header}
                                        </TableHeader>
                                    );
                                })}
                                {table.generatedFromFunction && table.summary?.type === 'COLUMN' && (
                                    <TableHeader key={'sumCol'} cellIndex={columns.length + 2} align={'right'} pinned={false}>
                                        <label className="text-xs text-gray-800 font-medium uppercase">Total</label>
                                    </TableHeader>
                                )}
                            </TableHead>
                            <TableBody>
                                {table.properties.map((row, idx) => {
                                    const summary = table.summary?.type === 'COLUMN' && table.summary?.properties[idx.toString()];
                                    return (
                                        <TableRow key={row.id} rowId={row.id}>
                                            {sortByOrder(row.properties)
                                                .filter((property) => (property.hidden ? showHidden : true))
                                                .map((property, idx) => {
                                                    return (
                                                        <TableCell key={property.id} cellIndex={idx + 1}>
                                                            <TableField
                                                                property={property}
                                                                inGenerated={Boolean(table.generatedFromFunction)}
                                                            />
                                                        </TableCell>
                                                    );
                                                })}
                                            {table.summary &&
                                                table.summary.type === 'COLUMN' &&
                                                (summary ? (
                                                    <TableCell key={summary.id} cellIndex={Object.keys(row.properties).length + 2}>
                                                        <TableField
                                                            property={{ ...summary, styles: { width: 'auto', alignment: 'right' } }}
                                                            inGenerated={false}
                                                        />
                                                    </TableCell>
                                                ) : (
                                                    <TableCell key={idx} cellIndex={Object.keys(row.properties).length + 2}></TableCell>
                                                ))}
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                            {/* Table Summary */}
                            {table.summary && !table.summary.type && table.properties.length > 0 && <TableSummary table={table} />}
                        </DTable>
                        {/* Empty State */}
                        {table.properties.length === 0 && (
                            <div className="mt-4 mx-auto w-fit">
                                <ButtonV2 label="New Row" size="sm" type="secondary" leftIcon={<PlusIcon />} onClick={handleNew} />
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

export default Table;
